import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useAuthService } from '~/composables/useAuthService';
import { useMessagingDatabase } from '~/composables/useMessagingDatabase';
import { useMessagingApi } from '~/composables/useMessagingApi';
import { useMessagingSignalR } from '~/composables/useMessagingSignalR';
import { MessageType } from '~/constants/messageTypes';
import { unreadCountOf } from '~/composables/useMessageTombstone.js';
import { deleteForEveryone } from '~/composables/useMessageDelete.js';
import { startConversation, CONVERSATION_FAILED_TEXT } from '~/composables/useConversationStart.js';

// Shared state (survives across component instances)
const conversations = ref([]);
const activeConversationId = ref(null);
const activeMessages = ref([]);
const typingUsers = ref(new Map()); // conversationId -> Set of { userId, firstName, lastName, startedAt }
const syncInProgress = ref(false);
const isInitialized = ref(false);
// Guard so the global watchers + initial load are wired up exactly once. MUST be a plain
// client-only module flag, NOT a Nuxt useState: useState serialises from SSR (where it gets
// set true but onMounted never fires), which makes the client skip registration entirely —
// so nothing loads and the unread badge never appears until the messaging dialog is opened.
let watchersRegistered = false;
const entityChatLoading = ref(false);
const entityChatErrors = ref({});
const isWindowVisible = ref(true); // Page Visibility API state
const isConversationInView = ref(false); // Track if MessageThread component is mounted
const imageUploadQueue = ref([]); // Image upload queue
const processingQueue = ref(false); // Flag to prevent concurrent queue processing

let db = null;

// Object-URL cache: one blob URL per message, reused across enrichment passes. They
// were previously minted fresh on every re-enrichment and never revoked (slow leak).
const blobUrlCache = new Map(); // messageId -> object URL
const getBlobUrl = (messageId, blob) => {
    if (!blobUrlCache.has(messageId)) {
        blobUrlCache.set(messageId, URL.createObjectURL(blob));
    }
    return blobUrlCache.get(messageId);
};
const revokeAllBlobUrls = () => {
    for (const url of blobUrlCache.values()) {
        URL.revokeObjectURL(url);
    }
    blobUrlCache.clear();
};

let typingSweepTimer = null;
let markAsReadDebounceTimer = null;

export const useMessaging = () => {
    const { user, isImpersonating } = useAuthService();
    const api = useMessagingApi();
    const signalR = useMessagingSignalR();
    const { setAlertMessage } = useAlertService();

    // Computed
    const unreadCount = computed(() => {
        return conversations.value.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);
    });

    const activeConversation = computed(() => {
        return conversations.value.find(c => c.id === activeConversationId.value) || null;
    });

    // Debouncing for sync operations
    const syncDebounceTimer = ref(null);
    const SYNC_DEBOUNCE_MS = 500;

    // Generate UUID v4
    const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };

    /**
     * Initialize database connection
     */
    const initDatabase = async () => {
        if (!db) {
            db = await useMessagingDatabase();
        }
        return db;
    };

    /**
     * Process image upload queue sequentially
     * Uploads images one at a time and creates messages
     */
    const processImageQueue = async () => {
        if (!user.value?.id || processingQueue.value) return;

        processingQueue.value = true;
        let shouldRetry = false;

        try {
            await initDatabase();

            // Get pending queue items
            const pendingItems = await db.getImageQueue(user.value.id, 'pending');
            console.log(`Processing ${pendingItems.length} pending image(s) in queue`);

            for (const queueItem of pendingItems) {
                try {
                    console.log('Processing queue item:', queueItem.id);
                    // Update status to uploading
                    await db.updateQueueItemStatus(user.value.id, queueItem.id, 'uploading');
                    imageUploadQueue.value = await db.getImageQueue(user.value.id);

                    // Convert Blob to base64 with MIME type
                    const base64Data = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            // Keep full data URL with MIME type (e.g., "data:image/png;base64,...")
                            resolve(reader.result);
                        };
                        reader.onerror = reject;
                        reader.readAsDataURL(queueItem.fileBlob);
                    });

                    // Upload image
                    console.log('Uploading image to backend...');
                    const uploadResponse = await api.uploadImage(base64Data, queueItem.fileName);
                    console.log('Upload response:', uploadResponse);

                    if (!uploadResponse?.id) {
                        throw new Error('Invalid upload response');
                    }

                    // Update queue item with imageUploadId
                    await db.updateQueueItemStatus(
                        user.value.id,
                        queueItem.id,
                        'uploading',
                        null,
                        uploadResponse.id
                    );

                    // Create message with imageUploadId
                    console.log('Creating message with imageUploadId:', uploadResponse.id);
                    const response = await api.sendMessage({
                        conversationId: queueItem.conversationId,
                        content: queueItem.caption || '',
                        messageType: MessageType.IMAGE,
                        imageUploadId: uploadResponse.id
                    });
                    console.log('Message created successfully:', response.id);

                    // Update optimistic message with real data
                    if (queueItem.optimisticMessageId) {
                        await db.replaceMessageId(
                            user.value.id,
                            queueItem.optimisticMessageId,
                            response.id,
                            response
                        );
                    } else {
                        // If no optimistic message, just add the real one
                        await db.upsertMessages(user.value.id, [response]);
                    }

                    // Remove from queue
                    await db.removeFromQueue(user.value.id, queueItem.id);

                    // Broadcast via SignalR
                    await signalR.broadcastMessage(queueItem.conversationId, response);

                    // Reload UI if this is for active conversation
                    if (activeConversationId.value === queueItem.conversationId) {
                        const rawMessages = await db.getMessages(user.value.id, queueItem.conversationId);
                        let enriched = await enrichMessages(rawMessages);
                        enriched = await enrichMessagesWithReadReceipts(enriched, queueItem.conversationId);
                        activeMessages.value = enriched;
                    }

                    // Reload conversations to update last message
                    await loadFromIndexedDB(user.value.id);

                } catch (error) {
                    console.error('Failed to process queue item:', queueItem.id, error);

                    // Extract error message from backend response
                    let errorMessage = 'Failed to upload image';

                    // Handle Response objects (defensive - uploadImage should convert these)
                    if (error instanceof Response) {
                        // Only try to parse if body hasn't been consumed
                        if (!error.bodyUsed) {
                            try {
                                const errorData = await error.json();
                                if (errorData?.global?.[0]) {
                                    errorMessage = errorData.global[0];
                                } else if (errorData?.exception) {
                                    errorMessage = errorData.exception;
                                }
                            } catch (parseError) {
                                console.error('Failed to parse error response:', parseError);
                                errorMessage = `Upload failed (${error.status} ${error.statusText})`;
                            }
                        } else {
                            // Body already consumed, use status info
                            errorMessage = `Upload failed (${error.status} ${error.statusText})`;
                        }
                    } else if (error?.global?.[0]) {
                        errorMessage = error.global[0];
                    } else if (error?.exception) {
                        errorMessage = error.exception;
                    } else if (error?.message) {
                        errorMessage = error.message;
                    }

                    // Increment retry count
                    const updatedItem = await db.getQueueItem(user.value.id, queueItem.id);
                    const retryCount = (updatedItem?.retryCount || 0) + 1;

                    console.log(`Image upload failed (attempt ${retryCount}/3):`, errorMessage);

                    // Max 3 retries
                    if (retryCount >= 3) {
                        console.error('Max retries reached for image upload:', queueItem.id);
                        await db.updateQueueItemStatus(
                            user.value.id,
                            queueItem.id,
                            'failed',
                            errorMessage
                        );

                        // Update optimistic message to show error
                        if (queueItem.optimisticMessageId) {
                            console.log('Updating message to failed state:', queueItem.optimisticMessageId);

                            await db.updateMessageSyncState(
                                user.value.id,
                                queueItem.optimisticMessageId,
                                'failed',
                                errorMessage
                            );

                            // Reload to show error state
                            if (activeConversationId.value === queueItem.conversationId) {
                                const rawMessages = await db.getMessages(user.value.id, queueItem.conversationId);
                                let enriched = await enrichMessages(rawMessages);
                                enriched = await enrichMessagesWithReadReceipts(enriched, queueItem.conversationId);
                                activeMessages.value = enriched;
                            }

                            // Reload conversations to update last message
                            await loadFromIndexedDB(user.value.id);
                        } else {
                            console.warn('No optimistic message ID found for failed queue item');
                        }
                    } else {
                        // Reset to pending for retry
                        await db.updateQueueItemStatus(
                            user.value.id,
                            queueItem.id,
                            'pending',
                            `${errorMessage} (will retry)`
                        );

                        // Update retry count
                        const item = await db.getQueueItem(user.value.id, queueItem.id);
                        if (item) {
                            item.retryCount = retryCount;
                            await db.addImageToQueue(user.value.id, item);
                        }

                        // Mark that we need to retry after the queue finishes
                        shouldRetry = true;
                    }
                }

                // Update queue state
                imageUploadQueue.value = await db.getImageQueue(user.value.id);
            }

        } catch (error) {
            console.error('Error processing image queue:', error);
        } finally {
            processingQueue.value = false;

            // Update queue state
            if (user.value?.id) {
                await initDatabase();
                imageUploadQueue.value = await db.getImageQueue(user.value.id);
            }

            // Schedule retry if needed
            if (shouldRetry) {
                setTimeout(() => {
                    processImageQueue();
                }, 2000);
            }
        }
    };

    /**
     * Send an image message (adds to queue and triggers processing)
     * @param {string} conversationId - Conversation ID
     * @param {File|Blob} imageFile - Image file or Blob
     * @param {string} caption - Optional text caption
     * @returns {Promise<Object>} Optimistic message
     */
    const sendImageMessage = async (conversationId, imageFile, caption = '') => {
        if (!user.value?.id || !imageFile) return null;

        const targetConversationId = conversationId || activeConversationId.value;
        if (!targetConversationId) return null;

        await initDatabase();

        // Create optimistic message
        const optimisticId = generateUUID();
        const now = new Date().toISOString();
        const optimisticMessage = {
            id: optimisticId,
            conversationId: String(targetConversationId),
            senderUserId: String(user.value.id),
            content: String(caption || ''),
            messageType: MessageType.IMAGE,
            createdAt: now,
            updatedAt: now,
            deletedAt: null,
            imageUploadId: null, // Will be set after upload
            imageFileName: null,
            imageWidth: null,
            imageHeight: null,
            sender: {
                id: String(user.value.id),
                firstName: String(user.value.firstName || ''),
                lastName: String(user.value.lastName || ''),
                imageFileName: user.value.imageFileName || null
            },
            readByCount: 0,
            isReadByCurrentUser: true,
            _optimisticId: optimisticId,
            _localImageUrl: getBlobUrl(optimisticId, imageFile) // For preview while uploading
        };

        // Add optimistic message to IndexedDB
        await db.addMessage(user.value.id, optimisticMessage);

        // Update conversation with last message info
        const conversation = await db.getConversation(user.value.id, targetConversationId);
        if (conversation) {
            await db.upsertConversations(user.value.id, [{
                ...conversation,
                lastMessageAt: now,
                lastMessageContent: caption || '[Image]',
                lastMessageSenderUserId: user.value.id
            }]);
        }

        // Update UI immediately
        if (activeConversationId.value === targetConversationId) {
            const rawMessages = await db.getMessages(user.value.id, targetConversationId);
            let enriched = await enrichMessages(rawMessages);
            enriched = await enrichMessagesWithReadReceipts(enriched, targetConversationId);
            activeMessages.value = enriched;
        }
        await loadFromIndexedDB(user.value.id);

        // Add to upload queue
        const queueItem = {
            id: generateUUID(),
            conversationId: String(targetConversationId),
            fileBlob: imageFile, // Store as Blob in IndexedDB
            fileName: imageFile.name || 'image.jpg',
            caption: caption || '',
            status: 'pending',
            optimisticMessageId: optimisticId,
            retryCount: 0,
            createdAt: now
        };

        await db.addImageToQueue(user.value.id, queueItem);
        imageUploadQueue.value = await db.getImageQueue(user.value.id);

        // Trigger queue processing (non-blocking)
        processImageQueue().catch(error => {
            console.error('Error processing image queue:', error);
        });

        return optimisticMessage;
    };

    /**
     * Calculate unread count for a conversation based on read receipts
     */
    const calculateUnreadCount = async (userId, conversationId) => {
        // Messages from others after the user's read receipt (all of them
        // without one). A deleted message is never new (unreadCountOf).
        const reads = await db.getConversationReads(userId, conversationId);
        const userRead = reads.find(r => r.userId === userId);
        const messages = await db.getMessages(userId, conversationId);
        return unreadCountOf(messages, userId, userRead?.readUpToTimestamp ?? null);
    };

    /**
     * Load conversations and messages from IndexedDB
     * Enriches conversations with participant user data and calculates unread counts
     */
    const loadFromIndexedDB = async (userId) => {
        if (!userId) return;

        await initDatabase();

        // Load conversations
        const rawConversations = await db.getConversations(userId);

        // Extract all unique participant user IDs from all conversations
        const userIdsSet = new Set();
        for (const conv of rawConversations) {
            if (conv.participantUserIds && Array.isArray(conv.participantUserIds)) {
                conv.participantUserIds.forEach(id => userIdsSet.add(id));
            }
        }

        // Batch load all users
        const allUsers = userIdsSet.size > 0
            ? await db.getUsers(Array.from(userIdsSet))
            : [];

        // Create user lookup map for O(1) access
        const userMap = new Map(allUsers.map(u => [u.id, u]));

        // Enrich conversations with participant user objects and calculated unread count
        const enrichedConversations = await Promise.all(rawConversations.map(async conv => {
            // Calculate unread count from read receipts
            const unreadCount = await calculateUnreadCount(userId, conv.id);

            return {
                ...conv,
                participants: (conv.participantUserIds || [])
                    .map(id => userMap.get(id))
                    .filter(Boolean), // Remove any null/undefined (user not found in cache)
                unreadCount // Override with calculated value
            };
        }));

        conversations.value = enrichedConversations;

        // Load active conversation messages if one is selected
        if (activeConversationId.value) {
            const rawMessages = await db.getMessages(userId, activeConversationId.value);
            let enriched = await enrichMessages(rawMessages);
            enriched = await enrichMessagesWithReadReceipts(enriched, activeConversationId.value);
            activeMessages.value = enriched;
        }
    };

    /**
     * Enrich messages with sender user data and image previews for failed uploads
     */
    const enrichMessages = async (messages) => {
        if (!messages || messages.length === 0) return [];

        await initDatabase();

        // Extract unique sender user IDs
        const senderUserIds = [...new Set(messages.map(m => m.senderUserId).filter(Boolean))];

        // Batch load sender users
        const senders = senderUserIds.length > 0 ? await db.getUsers(senderUserIds) : [];

        // Create sender lookup map
        const senderMap = new Map(senders.map(u => [u.id, u]));

        // Get all queue items to find image blobs for pending/failed uploads
        const allQueueItems = await db.getImageQueue(user.value.id);

        // Enrich messages with sender data and image previews
        return messages.map(msg => {
            const enriched = {
                ...msg,
                sender: senderMap.get(msg.senderUserId)
            };

            // For image messages that are pending or failed, add local image URL from queue blob
            // Only create if one doesn't already exist (don't overwrite existing blob URLs)
            if (msg.messageType === 2 && (msg._syncState === 'pending' || msg._syncState === 'failed') && !enriched._localImageUrl) {
                const queueItem = allQueueItems.find(item => item.optimisticMessageId === msg.id);
                console.log('Looking for queue item for message:', msg.id, 'Found:', !!queueItem, 'Has blob:', !!queueItem?.fileBlob);
                if (queueItem?.fileBlob) {
                    // Create blob URL for preview
                    enriched._localImageUrl = getBlobUrl(msg.id, queueItem.fileBlob);
                    console.log('Created blob URL for failed/pending image:', enriched._localImageUrl);
                }
            }

            return enriched;
        });
    };

    /**
     * Enrich messages with read receipt data
     * Adds readByUserIds, readByCount, and isReadByCurrentUser to each message
     */
    const enrichMessagesWithReadReceipts = async (messages, conversationId) => {
        if (!messages || messages.length === 0 || !conversationId) return messages;

        await initDatabase();

        // Get all read receipts for this conversation
        const reads = await db.getConversationReads(user.value.id, conversationId);

        // Find current user's read receipt
        const currentUserRead = reads.find(r => r.userId === user.value.id);

        // Enrich each message with read data
        return messages.map(msg => {
            const createdAt = new Date(msg.createdAt);

            // Find all users who have read past this message (readUpToTimestamp >= createdAt)
            const readByUserIds = reads
                .filter(read => new Date(read.readUpToTimestamp) >= createdAt)
                .map(read => read.userId);

            // Count OTHER users who have read (exclude current user and message sender)
            const readByCount = readByUserIds.filter(
                id => id !== user.value.id && id !== msg.senderUserId
            ).length;

            // Check if current user has read this message
            // If current user has a read receipt, use it. Otherwise undefined (not yet synced)
            let isReadByCurrentUser = undefined;
            if (currentUserRead) {
                isReadByCurrentUser = new Date(currentUserRead.readUpToTimestamp) >= createdAt;
            }

            return {
                ...msg,
                readByUserIds, // Array of user IDs who have read (for on-demand loading)
                readByCount,
                isReadByCurrentUser
            };
        });
    };

    /**
     * Sync messages with backend
     */
    const syncMessages = async () => {
        if (!user.value?.id || syncInProgress.value) return;

        // Pin the identity for the whole sync: user.value can change mid-flight
        // (impersonation / account switch) and writing the response under the NEW
        // user's key would leak the old account's conversations into their cache.
        const uid = user.value.id;

        syncInProgress.value = true;
        try {
            await initDatabase();
            const lastSyncedAt = await db.getLastSyncedAt(uid);

            const response = await api.syncMessages(lastSyncedAt);

            if (user.value?.id !== uid) {
                console.warn('User changed during sync — discarding response');
                return;
            }

            // Update IndexedDB with synced data
            if (response.conversations && response.conversations.length > 0) {
                await db.upsertConversations(uid, response.conversations);
            }

            if (response.messages && response.messages.length > 0) {
                await db.upsertMessages(uid, response.messages);
            }

            // Store conversation reads (new in v3)
            if (response.conversationReads && response.conversationReads.length > 0) {
                await db.upsertConversationReads(uid, response.conversationReads);
            }

            // Cache users from sync response
            if (response.users && response.users.length > 0) {
                await db.upsertUsers(response.users);
            }

            // Update last synced timestamp
            await db.setLastSyncedAt(uid, response.syncedAt);

            // Reload from IndexedDB
            await loadFromIndexedDB(uid);

        } catch (error) {
            console.error('Error syncing messages:', error);
            // Surface it — a silent failed catch-up looks identical to "no new messages"
            setAlertMessage({
                message: 'Messages may be out of date. Reconnecting…',
                color: 'warning',
                location: 'bottom',
                icon: 'mdi-sync-alert'
            });
        } finally {
            syncInProgress.value = false;
        }
    };

    /**
     * Retry pending operations
     */
    const retryPendingOperations = async () => {
        if (!user.value?.id) return;

        await initDatabase();

        // Retry pending messages
        const pendingMessages = await db.getPendingMessages(user.value.id);
        for (const message of pendingMessages) {
            try {
                await retrySendMessage(message);
            } catch (error) {
                console.error('Failed to retry message:', message.id, error);
            }
        }

        // Retry pending conversations
        const pendingConversations = await db.getPendingConversations(user.value.id);
        for (const conversation of pendingConversations) {
            try {
                await retryCreateConversation(conversation);
            } catch (error) {
                console.error('Failed to retry conversation:', conversation.id, error);
            }
        }
    };

    /**
     * Retry sending a failed message
     */
    const retrySendMessage = async (message) => {
        try {
            // Handle image messages differently - need to retry the upload queue
            if (message.messageType === 2) {
                console.log('Retrying image message:', message.id);

                // Find the failed queue item for this message
                const allQueueItems = await db.getImageQueue(user.value.id);
                const queueItem = allQueueItems.find(item => item.optimisticMessageId === message.id);

                if (queueItem) {
                    console.log('Found queue item:', queueItem.id, 'with status:', queueItem.status);

                    // Reset queue item to pending with retry count = 0
                    await db.updateQueueItemStatus(
                        user.value.id,
                        queueItem.id,
                        'pending',
                        null
                    );

                    // Reset retry count
                    const item = await db.getQueueItem(user.value.id, queueItem.id);
                    if (item) {
                        item.retryCount = 0;
                        await db.addImageToQueue(user.value.id, item);
                    }

                    // Reset message back to pending
                    await db.updateMessageSyncState(user.value.id, message.id, 'pending', null);

                    // Reload messages to show uploading state
                    if (activeConversationId.value === message.conversationId) {
                        const rawMessages = await db.getMessages(user.value.id, message.conversationId);
                        let enriched = await enrichMessages(rawMessages);
                        enriched = await enrichMessagesWithReadReceipts(enriched, message.conversationId);
                        activeMessages.value = enriched;
                    }

                    // Trigger the queue processor
                    console.log('Triggering queue processor for retry...');
                    processImageQueue();
                } else {
                    console.error('No queue item found for message:', message.id);
                    throw new Error('No queue item found for this image message');
                }

                return;
            }

            // Handle text messages
            const response = await api.sendMessage({
                conversationId: message.conversationId,
                content: message.content,
                messageType: message.messageType || 1
            });

            // Replace optimistic ID with real ID
            await db.replaceMessageId(user.value.id, message.id, response.id, response);

            // Broadcast via SignalR
            await signalR.broadcastMessage(message.conversationId, response);

            // Reload messages for active conversation
            if (activeConversationId.value === message.conversationId) {
                activeMessages.value = await db.getMessages(user.value.id, message.conversationId);
            }

            // Reload conversations to update last message (with enriched participant data)
            await loadFromIndexedDB(user.value.id);

        } catch (error) {
            // Mark as failed
            await db.updateMessageSyncState(user.value.id, message.id, 'failed', error.message || 'Failed to send');
            await loadFromIndexedDB(user.value.id);
            throw error;
        }
    };

    /**
     * Retry creating a failed conversation
     */
    const retryCreateConversation = async (conversation) => {
        try {
            const response = await api.createConversation({
                conversationType: conversation.conversationType,
                title: conversation.title,
                participantUserIds: conversation.participantUserIds || []
            });

            // Replace optimistic ID with real ID
            await db.replaceConversationId(user.value.id, conversation.id, response.id, response);

            // Reload conversations (with enriched participant data)
            await loadFromIndexedDB(user.value.id);

            return response;

        } catch (error) {
            // Mark as failed
            await db.updateConversationSyncState(user.value.id, conversation.id, 'failed', error.message || 'Failed to create');
            await loadFromIndexedDB(user.value.id);
            throw error;
        }
    };

    /**
     * Send a new message (optimistic)
     * @param {string} content - Message content
     * @param {string|null} conversationId - Conversation ID (uses active if null)
     * @param {number} messageType - Message type (defaults to TEXT)
     * @param {string|null} imageUploadId - Image upload ID for image messages
     */
    const sendMessage = async (content, conversationId = null, messageType = MessageType.TEXT, imageUploadId = null) => {
        if (!user.value?.id) return null;
        if (!content?.trim() && messageType === MessageType.TEXT) return null;

        const targetConversationId = conversationId || activeConversationId.value;
        if (!targetConversationId) return null;

        await initDatabase();

        // Create optimistic message with plain values matching backend structure
        const optimisticId = generateUUID();
        const now = new Date().toISOString();
        const optimisticMessage = {
            id: optimisticId,
            conversationId: String(targetConversationId),
            senderUserId: String(user.value.id),
            content: String(content?.trim() || ''),
            messageType: messageType,
            createdAt: now,
            updatedAt: now,
            deletedAt: null,
            imageUploadId: imageUploadId,
            sender: {
                id: String(user.value.id),
                firstName: String(user.value.firstName || ''),
                lastName: String(user.value.lastName || ''),
                imageFileName: user.value.imageFileName || null
            },
            readByCount: 0,
            isReadByCurrentUser: true,
            _optimisticId: optimisticId
        };

        // Add to IndexedDB immediately (pending state)
        await db.addMessage(user.value.id, optimisticMessage);

        // Update conversation with last message info (optimistic)
        const conversation = await db.getConversation(user.value.id, targetConversationId);
        if (conversation) {
            await db.upsertConversations(user.value.id, [{
                ...conversation,
                lastMessageAt: now,
                lastMessageContent: content?.trim() || '[Image]',
                lastMessageSenderUserId: user.value.id
            }]);
        }

        // Update UI immediately
        if (activeConversationId.value === targetConversationId) {
            activeMessages.value = await db.getMessages(user.value.id, targetConversationId);
        }
        // Reload conversations to show updated last message (with enriched participant data)
        await loadFromIndexedDB(user.value.id);

        // Send to API in background
        try {
            const requestData = {
                conversationId: targetConversationId,
                content: content?.trim() || '',
                messageType: messageType
            };

            // Add imageUploadId if provided
            if (imageUploadId) {
                requestData.imageUploadId = imageUploadId;
            }

            const response = await api.sendMessage(requestData);

            // Replace optimistic message with real one
            await db.replaceMessageId(user.value.id, optimisticId, response.id, response);

            // Broadcast via SignalR
            await signalR.broadcastMessage(targetConversationId, response);

            // Reload UI
            if (activeConversationId.value === targetConversationId) {
                activeMessages.value = await db.getMessages(user.value.id, targetConversationId);
            }
            // Reload conversations (with enriched participant data)
            await loadFromIndexedDB(user.value.id);

            return response;

        } catch (error) {
            console.error('Failed to send message:', error);
            // Mark as failed in IndexedDB. Wrapped, because a failure HERE used to
            // replace the server's refusal with an IndexedDB DataError on its way
            // out, so the caller could not tell the sender why their message was
            // refused.
            try {
                await db.updateMessageSyncState(user.value.id, optimisticId, 'failed', error.message || 'Failed to send');
            } catch (dbError) {
                console.error('Could not record the failed message:', dbError);
            }

            // Put the chat list's preview back. The optimistic write above set it
            // to a message that never sent, so without this the list went on
            // showing a refused message as the latest one — the sender reads
            // "can I still message you? · Now" and believes it went.
            try {
                if (conversation) {
                    await db.upsertConversations(user.value.id, [conversation]);
                }
            } catch (dbError) {
                console.error('Could not restore the conversation preview:', dbError);
            }

            // Reload to show error state
            if (activeConversationId.value === targetConversationId) {
                activeMessages.value = await db.getMessages(user.value.id, targetConversationId);
            }
            await loadFromIndexedDB(user.value.id);

            throw error;
        }
    };

    /**
     * Create a new conversation
     */
    const createConversation = async (participantUserIds, conversationType = 1, title = null) => {
        console.log('createConversation called with:', { participantUserIds, conversationType, title });

        if (!user.value?.id) {
            console.error('No user ID found');
            return null;
        }

        console.log('Initializing database...');
        await initDatabase();

        // Convert reactive arrays/objects to plain values for IndexedDB
        let plainParticipantIds = Array.isArray(participantUserIds)
            ? participantUserIds.map(id => String(id))
            : [String(participantUserIds)];

        // For one-to-one conversations, ensure current user is included
        if (conversationType === 1) {
            const currentUserId = String(user.value.id);
            // If only one participant provided (the other user), add current user
            if (plainParticipantIds.length === 1 && !plainParticipantIds.includes(currentUserId)) {
                plainParticipantIds = [currentUserId, ...plainParticipantIds];
                console.log('Added current user to participants:', plainParticipantIds);
            }
        }

        // For one-to-one conversations, check if one already exists locally
        if (conversationType === 1 && plainParticipantIds.length === 2) {
            const currentUserId = String(user.value.id);
            const targetUserId = plainParticipantIds.find(id => id !== currentUserId);

            const existingConversation = conversations.value.find(conv =>
                conv.conversationType === 1 &&
                conv.participantUserIds?.length === 2 &&
                conv.participantUserIds.includes(currentUserId) &&
                conv.participantUserIds.includes(targetUserId)
            );

            if (existingConversation) {
                console.log('Found existing one-to-one conversation:', existingConversation.id);
                // Set as active and return existing conversation
                activeConversationId.value = existingConversation.id;

                // Join SignalR group (non-blocking)
                signalR.joinConversation(existingConversation.id).catch(err => {
                    console.warn('Failed to join SignalR conversation (non-critical):', err);
                });

                return existingConversation;
            }
        }

        console.log('Plain participant IDs:', plainParticipantIds);

        // Create optimistic conversation matching backend structure
        const optimisticId = generateUUID();
        const now = new Date().toISOString();
        const optimisticConversation = {
            id: optimisticId,
            conversationType: Number(conversationType),
            title: title ? String(title) : null,
            createdByUserId: String(user.value.id),
            lastMessageAt: null,
            createdAt: now,
            updatedAt: now,
            deletedAt: null,
            participantUserIds: plainParticipantIds,
            unreadCount: 0,
            _optimisticId: optimisticId
        };

        // Create via API. A refusal (a block, no shared club) takes the
        // temporary copy away again and throws the server's own sentence;
        // nothing opens (startConversation, 29 Sep 2026).
        try {
            const requestData = {
                conversationType: Number(conversationType),
                title: title ? String(title) : null,
                participantUserIds: plainParticipantIds
            };

            const response = await startConversation({
                temporary: optimisticConversation,
                addTemporary: async (conversation) => {
                    await db.addConversation(user.value.id, conversation);
                    await loadFromIndexedDB(user.value.id);
                },
                removeTemporary: (id) => db.deleteConversation(user.value.id, id),
                replaceTemporary: (id, reply) => db.replaceConversationId(user.value.id, id, reply.id, reply),
                callApi: () => api.createConversation(requestData),
            });

            await loadFromIndexedDB(user.value.id);

            // Set as active
            console.log('Setting active conversation to:', response.id);
            activeConversationId.value = response.id;

            // Join SignalR group (non-blocking, global SignalR should be connected elsewhere)
            console.log('Joining SignalR conversation:', response.id);
            signalR.joinConversation(response.id).catch(err => {
                console.warn('Failed to join SignalR conversation (non-critical):', err);
            });

            console.log('Conversation created successfully:', response);
            return response;

        } catch (error) {
            // The server's sentence, shown once here; callers only stop.
            setAlertMessage(error?.message || CONVERSATION_FAILED_TEXT);
            await loadFromIndexedDB(user.value.id);
            throw error;
        }
    };

    /**
     * Set active conversation
     */
    const setActiveConversation = async (conversationId) => {
        if (!user.value?.id) return;

        // Leave previous conversation group (non-blocking)
        if (activeConversationId.value && activeConversationId.value !== conversationId) {
            signalR.leaveConversation(activeConversationId.value).catch(err => {
                console.warn('Failed to leave SignalR conversation (non-critical):', err);
            });
        }

        activeConversationId.value = conversationId;

        if (conversationId) {
            await initDatabase();

            // Load and enrich messages with sender data and read receipts
            const rawMessages = await db.getMessages(user.value.id, conversationId);
            let enriched = await enrichMessages(rawMessages);
            enriched = await enrichMessagesWithReadReceipts(enriched, conversationId);
            activeMessages.value = enriched;

            // Join SignalR group (non-blocking)
            signalR.joinConversation(conversationId).catch(err => {
                console.warn('Failed to join SignalR conversation (non-critical):', err);
            });

            // Set conversation in view
            isConversationInView.value = true;

            // Mark messages as read if window is visible
            checkAndMarkAsRead();
        } else {
            activeMessages.value = [];
            isConversationInView.value = false;
        }
    };

    /**
     * Update conversation participants
     * @param {string} conversationId
     * @param {Array<string>} participantUserIds - The new complete list of participant IDs
     */
    const updateConversationParticipants = async (conversationId, participantUserIds) => {
        if (!user.value?.id) return;
        await initDatabase();

        try {
            // Get current conversation to determine what changed
            const currentConversation = conversations.value.find(c => c.id === conversationId);
            if (!currentConversation) return;

            const currentParticipantIds = currentConversation.participantUserIds || [];

            // Determine who to add and who to remove
            const toAdd = participantUserIds.filter(id => !currentParticipantIds.includes(id));
            const toRemove = currentParticipantIds.filter(id => !participantUserIds.includes(id));

            let lastResponse;

            // Add new participants (use bulk endpoint if multiple)
            if (toAdd.length > 0) {
                if (toAdd.length === 1) {
                    lastResponse = await api.addParticipant(conversationId, toAdd[0]);
                } else {
                    lastResponse = await api.addParticipants(conversationId, toAdd);
                }

                // Cache users from response
                if (lastResponse.participants) {
                    const users = lastResponse.participants.map(p => p.user).filter(Boolean);
                    if (users.length > 0) {
                        await db.upsertUsers(users);
                    }
                }
            }

            // Remove participants
            for (const userId of toRemove) {
                lastResponse = await api.removeParticipant(conversationId, userId);

                // Cache users from response
                if (lastResponse.participants) {
                    const users = lastResponse.participants.map(p => p.user).filter(Boolean);
                    if (users.length > 0) {
                        await db.upsertUsers(users);
                    }
                }
            }

            // Update in IndexedDB with latest response
            if (lastResponse) {
                await db.upsertConversations(user.value.id, [lastResponse]);
            }

            // Reload conversations to reflect changes
            await loadFromIndexedDB(user.value.id);

            return lastResponse;
        } catch (error) {
            console.error('Failed to update conversation participants:', error);
            throw error;
        }
    };

    /**
     * Update conversation admins
     * @param {string} conversationId
     * @param {Array<string>} adminUserIds - The new complete list of admin user IDs
     */
    const updateConversationAdmins = async (conversationId, adminUserIds) => {
        if (!user.value?.id) return;
        await initDatabase();

        try {
            // Get current conversation to determine what changed
            const currentConversation = conversations.value.find(c => c.id === conversationId);
            if (!currentConversation) return;

            const currentAdminIds = currentConversation.adminUserIds || [];
            const participantIds = currentConversation.participantUserIds || [];

            // Determine who to promote and who to demote
            const toPromote = adminUserIds.filter(id => !currentAdminIds.includes(id) && participantIds.includes(id));
            const toDemote = currentAdminIds.filter(id => !adminUserIds.includes(id));

            let lastResponse;

            // Promote to admin
            for (const userId of toPromote) {
                lastResponse = await api.setAdminStatus(conversationId, userId, true);

                // Cache users from response
                if (lastResponse.participants) {
                    const users = lastResponse.participants.map(p => p.user).filter(Boolean);
                    if (users.length > 0) {
                        await db.upsertUsers(users);
                    }
                }
            }

            // Demote from admin
            for (const userId of toDemote) {
                lastResponse = await api.setAdminStatus(conversationId, userId, false);

                // Cache users from response
                if (lastResponse.participants) {
                    const users = lastResponse.participants.map(p => p.user).filter(Boolean);
                    if (users.length > 0) {
                        await db.upsertUsers(users);
                    }
                }
            }

            // Update in IndexedDB with latest response
            if (lastResponse) {
                await db.upsertConversations(user.value.id, [lastResponse]);
            }

            // Reload conversations to reflect changes
            await loadFromIndexedDB(user.value.id);

            return lastResponse;
        } catch (error) {
            console.error('Failed to update conversation admins:', error);
            throw error;
        }
    };

    /**
     * Update conversation title
     * @param {string} conversationId
     * @param {string} title
     */
    const updateConversationTitle = async (conversationId, title) => {
        if (!user.value?.id) return;
        await initDatabase();

        try {
            // Update on backend
            const updatedConversation = await api.updateConversation(conversationId, { title });

            // Update in IndexedDB
            await db.upsertConversations(user.value.id, [updatedConversation]);

            // Reload conversations to reflect changes
            await loadFromIndexedDB(user.value.id);

            return updatedConversation;
        } catch (error) {
            console.error('Failed to update conversation title:', error);
            throw error;
        }
    };

    /**
     * Leave a conversation (removes current user from participants)
     * @param {string} conversationId
     */
    const leaveConversation = async (conversationId) => {
        if (!user.value?.id) return;
        await initDatabase();

        try {
            // Call backend to leave conversation
            const updatedConversation = await api.leaveConversation(conversationId);

            // Update in IndexedDB
            await db.upsertConversations(user.value.id, [updatedConversation]);

            // If left conversation is active, clear active conversation
            if (activeConversationId.value === conversationId) {
                activeConversationId.value = null;
                activeMessages.value = [];
            }

            // Reload conversations to reflect changes
            await loadFromIndexedDB(user.value.id);

            return updatedConversation;
        } catch (error) {
            console.error('Failed to leave conversation:', error);
            throw error;
        }
    };

    /**
     * Get notification status for a conversation
     * @param {string} conversationId
     * @returns {Promise<Object>} - { notificationsEnabled: boolean }
     */
    const getConversationNotifications = async (conversationId) => {
        try {
            const response = await api.getNotificationsEnabled(conversationId);
            return response;
        } catch (error) {
            console.error('Failed to get conversation notifications:', error);
            throw error;
        }
    };

    /**
     * Set notification status for a conversation
     * @param {string} conversationId
     * @param {boolean} notificationsEnabled
     * @returns {Promise<Object>} - Updated conversation
     */
    const setConversationNotifications = async (conversationId, notificationsEnabled) => {
        if (!user.value?.id) return;
        await initDatabase();

        try {
            // Update on backend
            const updatedConversation = await api.setNotificationsEnabled(conversationId, notificationsEnabled);

            // Update in IndexedDB
            await db.upsertConversations(user.value.id, [updatedConversation]);

            // Reload conversations to reflect changes
            await loadFromIndexedDB(user.value.id);

            return updatedConversation;
        } catch (error) {
            console.error('Failed to update conversation notifications:', error);
            throw error;
        }
    };

    /**
     * Create or open an entity-linked chat (ladder, league cycle, club, etc.)
     * Always calls the API to ensure user is synced and added to conversation
     * @param {Object} entityData - Object with entity ID and conversationId (e.g., { ladderId, conversationId })
     * @param {Function} onSuccess - Callback to refetch entity data after conversation creation
     */
    const createOrOpenEntityChat = async (entityData, onSuccess) => {
        const router = useRouter();
        const route = useRoute();

        entityChatLoading.value = true;
        entityChatErrors.value = {};

        try {
            // Extract conversationId from entityData and create payload
            const { conversationId, ...payload } = entityData;
            payload.conversationType = 2; // Group chat

            // Always call API to ensure user is synced/added to conversation
            const response = await api.createEntityConversation(payload);

            // Only proceed if we have a valid response
            if (!response || !response.id) {
                throw new Error('Invalid response from API');
            }

            // Call success callback to refetch entity data
            if (onSuccess) {
                await onSuccess();
            }

            // Navigate to the conversation
            router.push({
                query: { ...route.query, conversationId: response.id, messaging: 'open' }
            });
        } catch (error) {
            console.error('Error creating/joining entity chat:', error);
            entityChatErrors.value = error;

            // Show error message to user
            const errorMessage = error.global?.[0] || error.message || 'Failed to join chat';
            setAlertMessage({ message: errorMessage, color: 'error' });

            // Don't navigate or throw - just return early
            // The finally block will clean up loading state
        } finally {
            entityChatLoading.value = false;
        }
    };

    /**
     * Delete a conversation
     * - For one-to-one chats (conversationType 1): Delete locally only
     * - For group chats (conversationType 2+): Soft delete on backend
     */
    const deleteConversation = async (conversationId) => {
        if (!user.value?.id) return;

        await initDatabase();

        // Get conversation to check type
        const conversation = await db.getConversation(user.value.id, conversationId);
        if (!conversation) return;

        // Only call backend delete for group conversations
        // One-to-one conversations should only be deleted locally
        if (conversation.conversationType !== 1) {
            await api.deleteConversation(conversationId);
        }

        // Remove from IndexedDB (local delete)
        await db.deleteConversation(user.value.id, conversationId);

        // If deleted conversation is active, clear active conversation
        if (activeConversationId.value === conversationId) {
            activeConversationId.value = null;
            activeMessages.value = [];
        }

        // Reload conversations from IndexedDB
        await loadFromIndexedDB(user.value.id);
    };

    /**
     * Delete a failed message
     */
    const deleteFailedMessage = async (messageId) => {
        console.log('deleteFailedMessage called with messageId:', messageId);

        if (!user.value?.id) {
            console.log('No user ID, returning');
            return;
        }

        await initDatabase();
        console.log('Database initialized');

        // Find and delete associated queue item if this is an image message
        const allQueueItems = await db.getImageQueue(user.value.id);
        console.log('All queue items:', allQueueItems);
        const queueItem = allQueueItems.find(item => item.optimisticMessageId === messageId);
        console.log('Found queue item:', queueItem);

        if (queueItem) {
            console.log('Deleting queue item for failed message:', queueItem.id);
            await db.removeFromQueue(user.value.id, queueItem.id);
            imageUploadQueue.value = await db.getImageQueue(user.value.id);
            console.log('Queue item deleted, updated queue:', imageUploadQueue.value);
        } else {
            console.log('No queue item found for messageId:', messageId);
        }

        // Delete the message
        console.log('Deleting message from IndexedDB:', messageId);
        await db.deleteMessage(user.value.id, messageId);
        console.log('Message deleted from IndexedDB');

        // Reload and enrich active messages
        if (activeConversationId.value) {
            console.log('Reloading messages for conversation:', activeConversationId.value);
            const rawMessages = await db.getMessages(user.value.id, activeConversationId.value);
            console.log('Raw messages after delete:', rawMessages.length);
            let enriched = await enrichMessages(rawMessages);
            enriched = await enrichMessagesWithReadReceipts(enriched, activeConversationId.value);
            activeMessages.value = enriched;
            console.log('Active messages updated:', activeMessages.value.length);
        }

        // Reload conversations to update last message
        console.log('Reloading conversations');
        await loadFromIndexedDB(user.value.id);
        console.log('deleteFailedMessage completed');
    };

    /**
     * Delete my own SENT message, for everyone in the chat (owner decision,
     * 24 Sep 2026). The placeholder shows at once and the message is put back
     * if the server refuses. Offline is refused, not queued. Returns
     * {ok} or {ok: false, error} with the sentence to show.
     */
    const deleteSentMessage = async (message) => {
        if (!user.value?.id || !message?.id) return {ok: false, error: null};
        await initDatabase();
        const uid = user.value.id;
        const conversationId = message.conversationId;

        const refresh = async () => {
            if (activeConversationId.value === conversationId) {
                const rawMessages = await db.getMessages(uid, conversationId);
                let enriched = await enrichMessages(rawMessages);
                enriched = await enrichMessagesWithReadReceipts(enriched, conversationId);
                activeMessages.value = enriched;
            }
        };

        const result = await deleteForEveryone({
            message,
            online: typeof navigator === 'undefined' ? true : navigator.onLine,
            readStored: async (id) => (await db.getMessages(uid, conversationId)).find(m => m.id === id),
            writeStored: (m) => db.upsertMessages(uid, [m]),
            refresh,
            callApi: (id) => api.deleteMessage(id),
        });
        // Pull the server's tombstone and the chat list preview, which only the
        // server can say (the latest VISIBLE message decides it).
        if (result.ok) handleSyncRequired('message-deleted');
        return result;
    };

    /**
     * Send typing indicator
     */
    const sendTypingIndicator = async (isTyping, conversationId = null) => {
        const targetConversationId = conversationId || activeConversationId.value;
        if (targetConversationId) {
            await signalR.sendTypingIndicator(targetConversationId, isTyping);
        }
    };

    // SignalR Event Handlers
    const handleNewMessage = async (message) => {
        console.log('[HANDLER] handleNewMessage START', {
            messageId: message?.id,
            conversationId: message?.conversationId,
            senderUserId: message?.senderUserId,
            currentUserId: user.value?.id,
            timestamp: new Date().toISOString()
        });

        if (!user.value?.id) {
            console.warn('[HANDLER] handleNewMessage SKIPPED - No user ID');
            return;
        }

        // Skip messages from current user (they're already handled optimistically)
        if (message.senderUserId === user.value.id) {
            console.log('[HANDLER] handleNewMessage SKIPPED - Message from current user (optimistic)');
            return;
        }

        await initDatabase();

        // Only store messages for conversations this user actually has locally. A hub
        // event for an unknown conversation is either a brand-new membership (the
        // authorized sync will bring it) or an event addressed to a different identity
        // (e.g. the socket briefly authenticated as the admin around an impersonation
        // switch) — never write those into this user's cache.
        const knownConversation = await db.getConversation(user.value.id, message.conversationId);
        if (!knownConversation) {
            console.log('[HANDLER] handleNewMessage - Unknown conversation, deferring to sync');
            handleSyncRequired('unknown-conversation');
            return;
        }

        // Upsert message to IndexedDB (for both active and background conversations)
        await db.upsertMessages(user.value.id, [message]);

        // Handle active conversation instantly for immediate UI feedback
        if (activeConversationId.value === message.conversationId) {
            console.log('[HANDLER] handleNewMessage - Message is for ACTIVE conversation', {
                activeConversationId: activeConversationId.value,
                messageId: message.id
            });

            // Update lastMessageAt for sorting
            const convIndex = conversations.value.findIndex(c => c.id === message.conversationId);
            if (convIndex !== -1) {
                conversations.value[convIndex] = {
                    ...conversations.value[convIndex],
                    lastMessageAt: message.createdAt
                };
            }

            // Enrich and append just the incoming message — reloading and re-enriching
            // the whole thread per message made receiving O(thread length).
            let [enrichedMessage] = await enrichMessages([message]);
            [enrichedMessage] = await enrichMessagesWithReadReceipts([enrichedMessage], message.conversationId);
            activeMessages.value = [
                ...activeMessages.value.filter(m => m.id !== message.id && m._optimisticId !== message.id),
                enrichedMessage
            ];

            console.log('[HANDLER] handleNewMessage - Message appended, count:', activeMessages.value.length);

            // Auto-mark as read if window is visible and conversation is in view
            console.log('[HANDLER] handleNewMessage - Calling checkAndMarkAsRead');
            checkAndMarkAsRead();
        } else {
            console.log('[HANDLER] handleNewMessage - Message is for BACKGROUND conversation', {
                messageConversationId: message.conversationId,
                activeConversationId: activeConversationId.value
            });
            // For background conversations, recalculate unread count
            const convIndex = conversations.value.findIndex(c => c.id === message.conversationId);
            if (convIndex !== -1) {
                // Recalculate unread count based on read receipts
                const unreadCount = await calculateUnreadCount(user.value.id, message.conversationId);

                // Create new conversation object to trigger reactivity
                conversations.value[convIndex] = {
                    ...conversations.value[convIndex],
                    unreadCount,
                    lastMessageAt: message.createdAt
                };

                // Show notification
                setAlertMessage({
                    message: `New message from ${message.sender?.firstName || 'Someone'}`,
                    color: 'info',
                    location: 'bottom',
                    icon: 'mdi-chat'
                });
            }
        }
    };

    const handleMessageRead = async (data) => {
        // data: { conversationId, userId, readUpToTimestamp }
        console.log('[HANDLER] handleMessageRead START', {
            conversationId: data?.conversationId,
            userId: data?.userId,
            readUpToTimestamp: data?.readUpToTimestamp,
            currentUserId: user.value?.id,
            isForCurrentUser: data?.userId === user.value?.id,
            timestamp: new Date().toISOString()
        });

        if (!data.conversationId || !data.userId || !data.readUpToTimestamp) {
            console.warn('[HANDLER] handleMessageRead - Invalid data:', data);
            return;
        }

        try {
            await initDatabase();

            // Identity guard (see handleNewMessage): don't record receipts for
            // conversations this user doesn't have.
            const knownConversation = await db.getConversation(user.value.id, data.conversationId);
            if (!knownConversation) {
                return;
            }

            // Update read receipt in IndexedDB
            await db.setConversationRead(
                user.value.id,
                data.conversationId,
                data.userId,
                data.readUpToTimestamp
            );
            console.log('[HANDLER] handleMessageRead - IndexedDB updated with read receipt');

            // If this is for the active conversation, refresh messages with updated read receipts
            if (activeConversationId.value === data.conversationId) {
                console.log('[HANDLER] handleMessageRead - Refreshing active conversation messages');
                const rawMessages = await db.getMessages(user.value.id, data.conversationId);
                let enriched = await enrichMessages(rawMessages);
                enriched = await enrichMessagesWithReadReceipts(enriched, data.conversationId);
                activeMessages.value = enriched;
                console.log('[HANDLER] handleMessageRead - Active messages refreshed');
            }

            // Recalculate unread count if the read event is for the current user
            if (data.userId === user.value.id) {
                console.log('[HANDLER] handleMessageRead - Read event is for current user, recalculating unread count');
                const convIndex = conversations.value.findIndex(c => c.id === data.conversationId);
                if (convIndex !== -1) {
                    const unreadCount = await calculateUnreadCount(user.value.id, data.conversationId);
                    console.log('[HANDLER] handleMessageRead - New unread count:', unreadCount);

                    // Create new conversation object to trigger reactivity
                    conversations.value[convIndex] = {
                        ...conversations.value[convIndex],
                        unreadCount
                    };
                }
            } else {
                console.log('[HANDLER] handleMessageRead - Read event is for OTHER user:', data.userId);
            }
        } catch (error) {
            console.error('[HANDLER] handleMessageRead ERROR:', {
                error: error.message || error,
                data,
                timestamp: new Date().toISOString()
            });
        }
    };

    const handleUserTyping = (data) => {
        // data: { userId, isTyping, conversationId }
        // conversationId is REQUIRED - without it we can't scope typing to the correct conversation
        if (!data.conversationId) {
            console.warn('Typing event received without conversationId, ignoring');
            return;
        }

        const convId = data.conversationId;

        if (!typingUsers.value.has(convId)) {
            typingUsers.value.set(convId, new Set());
        }

        const usersTyping = typingUsers.value.get(convId);

        if (data.isTyping) {
            // One entry per user: repeated typing events refresh startedAt instead of
            // stacking duplicate objects in the Set.
            const existingEntry = Array.from(usersTyping).find(u => u.userId === data.userId);
            if (existingEntry) {
                usersTyping.delete(existingEntry);
            }
            usersTyping.add({
                userId: data.userId,
                startedAt: new Date()
            });
            // log the user who is typing for debugging
            console.log(`User ${data.userId} is typing in conversation ${convId}`);
        } else {
            // Remove user from typing
            const userEntry = Array.from(usersTyping).find(u => u.userId === data.userId);
            if (userEntry) {
                usersTyping.delete(userEntry);
            }
            // log the user who stopped typing for debugging
            console.log(`User ${data.userId} stopped typing in conversation ${convId}`);
        }

        // Trigger reactivity
        typingUsers.value = new Map(typingUsers.value);
    };

    const handleConversationUpdated = async (conversation) => {
        if (!user.value?.id) return;

        await initDatabase();

        // Same identity guard as handleNewMessage: unknown conversations go through the
        // authorized sync instead of being written straight into this user's cache.
        const knownConversation = await db.getConversation(user.value.id, conversation.id);
        if (!knownConversation) {
            handleSyncRequired('unknown-conversation-updated');
            return;
        }

        await db.upsertConversations(user.value.id, [conversation]);
        await loadFromIndexedDB(user.value.id);
    };

    // Debounced entry point: a burst of incoming messages used to fire one
    // mark-as-read API call per message; only the last one mattered.
    const checkAndMarkAsRead = () => {
        if (markAsReadDebounceTimer) {
            clearTimeout(markAsReadDebounceTimer);
        }
        markAsReadDebounceTimer = setTimeout(() => {
            markAsReadDebounceTimer = null;
            checkAndMarkAsReadNow();
        }, 300);
    };

    const handleSyncRequired = async (data) => {
        console.info('Sync required event received:', data);

        // Clear existing timer
        if (syncDebounceTimer.value) {
            clearTimeout(syncDebounceTimer.value);
        }

        // Debounce sync calls to prevent spam when multiple events arrive quickly
        syncDebounceTimer.value = setTimeout(async () => {
            await syncMessages();
            syncDebounceTimer.value = null;
        }, SYNC_DEBOUNCE_MS);
    };

    /**
     * Check if conditions are met to mark conversation as read
     * and call API if needed
     */
    const checkAndMarkAsReadNow = async () => {
        console.log('checkAndMarkAsRead called', {
            activeConversationId: activeConversationId.value,
            isWindowVisible: isWindowVisible.value,
            isConversationInView: isConversationInView.value,
            activeMessagesLength: activeMessages.value.length
        });

        if (!activeConversationId.value || !isWindowVisible.value || !isConversationInView.value) {
            console.log('Early return: missing required conditions');
            return;
        }

        // Find the latest message timestamp in active messages
        if (activeMessages.value.length === 0) {
            console.log('Early return: no messages');
            return;
        }

        const latestMessage = activeMessages.value[activeMessages.value.length - 1];
        if (!latestMessage?.createdAt) {
            console.log('Early return: no createdAt timestamp');
            return;
        }

        // Check if we already have a read receipt for the latest message
        await initDatabase();
        const reads = await db.getConversationReads(user.value.id, activeConversationId.value);
        const currentUserRead = reads.find(r => r.userId === user.value.id);

        console.log('Read receipt check', {
            latestMessageCreatedAt: latestMessage.createdAt,
            currentUserRead: currentUserRead,
            readUpToTimestamp: currentUserRead?.readUpToTimestamp
        });

        // If we already marked this timestamp as read, skip
        if (currentUserRead && new Date(currentUserRead.readUpToTimestamp) >= new Date(latestMessage.createdAt)) {
            console.log('Early return: already marked as read');
            return;
        }

        console.log('[MARK READ] Calling markConversationAsRead API with timestamp:', latestMessage.createdAt);

        try {
            // Call API to mark as read
            const response = await api.markConversationAsRead(activeConversationId.value, latestMessage.createdAt);
            console.log('[MARK READ] API call SUCCESS', {
                conversationId: activeConversationId.value,
                readUpToTimestamp: latestMessage.createdAt,
                response
            });

            // Update local IndexedDB with current user's read receipt
            await db.setConversationRead(
                user.value.id,
                activeConversationId.value,
                user.value.id,
                latestMessage.createdAt
            );
            console.log('[MARK READ] IndexedDB updated with read receipt');

            // Recalculate conversation unreadCount based on read receipts
            const convIndex = conversations.value.findIndex(c => c.id === activeConversationId.value);
            if (convIndex !== -1) {
                const unreadCount = await calculateUnreadCount(user.value.id, activeConversationId.value);
                console.log('[MARK READ] Recalculated unread count:', unreadCount);

                // Create new conversation object to trigger reactivity
                conversations.value[convIndex] = {
                    ...conversations.value[convIndex],
                    unreadCount
                };
            }

            // Refresh messages with updated read receipts
            const rawMessages = await db.getMessages(user.value.id, activeConversationId.value);
            let enriched = await enrichMessages(rawMessages);
            enriched = await enrichMessagesWithReadReceipts(enriched, activeConversationId.value);
            activeMessages.value = enriched;
            console.log('[MARK READ] Messages refreshed with updated read receipts');

        } catch (error) {
            console.error('[MARK READ] ERROR marking conversation as read:', {
                error: error.message || error,
                conversationId: activeConversationId.value,
                readUpToTimestamp: latestMessage.createdAt,
                timestamp: new Date().toISOString()
            });
        }
    };

    /**
     * Handle visibility change
     */
    const handleVisibilityChange = () => {
        isWindowVisible.value = !document.hidden;
        if (isWindowVisible.value) {
            // Window became visible - check if we need to mark as read
            checkAndMarkAsRead();

            // Background tabs get throttled/suspended and can miss syncrequired
            // nudges entirely — treat waking up like one (debounced like the rest).
            if (isInitialized.value) {
                handleSyncRequired('visibilitychange');
            }
        }
    };

    /**
     * Initialize messaging system
     */
    const initialize = async () => {
        if (!user.value?.id || isInitialized.value) return;

        console.info('Initializing messaging system for user:', user.value.id);

        // Load from IndexedDB
        await loadFromIndexedDB(user.value.id);

        // Load image upload queue
        imageUploadQueue.value = await db.getImageQueue(user.value.id);

        // Set up Page Visibility API listener
        document.addEventListener('visibilitychange', handleVisibilityChange);
        isWindowVisible.value = !document.hidden;

        // Sweep stale typing indicators: if a user's isTyping=false never arrives
        // (network drop), the indicator would otherwise linger forever.
        typingSweepTimer = setInterval(() => {
            const cutoff = Date.now() - 5000;
            let changed = false;
            for (const usersTyping of typingUsers.value.values()) {
                for (const entry of Array.from(usersTyping)) {
                    if (new Date(entry.startedAt).getTime() < cutoff) {
                        usersTyping.delete(entry);
                        changed = true;
                    }
                }
            }
            if (changed) {
                typingUsers.value = new Map(typingUsers.value);
            }
        }, 3000);

        // Note: SignalR connection is managed globally (in AppLayout.vue or app plugin)
        // We only register/unregister event handlers here

        // Register SignalR event handlers
        signalR.onNewMessage(handleNewMessage);
        signalR.onMessageRead(handleMessageRead);
        signalR.onUserTyping(handleUserTyping);
        signalR.onConversationUpdated(handleConversationUpdated);
        signalR.onSyncRequired(handleSyncRequired);

        // Sync with backend
        await syncMessages();

        // Retry any pending operations
        await retryPendingOperations();

        // Resume image upload queue if there are pending items
        if (imageUploadQueue.value.some(item => item.status === 'pending')) {
            console.info('Resuming image upload queue processing...');
            processImageQueue().catch(error => {
                console.error('Error processing image queue on initialization:', error);
            });
        }

        isInitialized.value = true;
    };

    /**
     * Cleanup messaging system
     */
    const cleanup = () => {
        console.info('Cleaning up messaging system');

        // Clear any pending debounce timers
        if (syncDebounceTimer.value) {
            clearTimeout(syncDebounceTimer.value);
            syncDebounceTimer.value = null;
        }
        if (markAsReadDebounceTimer) {
            clearTimeout(markAsReadDebounceTimer);
            markAsReadDebounceTimer = null;
        }
        if (typingSweepTimer) {
            clearInterval(typingSweepTimer);
            typingSweepTimer = null;
        }
        revokeAllBlobUrls();

        // Remove Page Visibility API listener
        document.removeEventListener('visibilitychange', handleVisibilityChange);

        // Remove SignalR handlers
        signalR.offNewMessage(handleNewMessage);
        signalR.offMessageRead(handleMessageRead);
        signalR.offUserTyping(handleUserTyping);
        signalR.offConversationUpdated(handleConversationUpdated);
        signalR.offSyncRequired(handleSyncRequired);

        // Leave active conversation
        if (activeConversationId.value) {
            signalR.leaveConversation(activeConversationId.value);
        }

        // Clear state
        conversations.value = [];
        activeConversationId.value = null;
        activeMessages.value = [];
        typingUsers.value = new Map();
        isInitialized.value = false;
    };

    // Use a shared state to ensure watchers are only created once globally
    // Register the global watchers + initial load once, on the CLIENT. Guarding on a
    // client-only flag (not useState) ensures this actually runs after hydration so the
    // unread count loads on a fresh visit, without waiting for the dialog to be opened.
    if (import.meta.client && !watchersRegistered) {
        watchersRegistered = true;

        // Watch for user changes (including impersonation, which swaps user.id)
        watch(() => [user.value?.id, isImpersonating.value],
            async ([newUserId, nowImpersonating], [oldUserId, wasImpersonating] = []) => {
            if (newUserId !== oldUserId) {
                cleanup();
                if (newUserId) {
                    // Crossing an impersonation boundary rewrites the effective identity.
                    // Purge the target user's local store before syncing: caches written
                    // while identities were misaligned (or before this guard existed) can
                    // hold the other account's conversations/messages.
                    if (nowImpersonating !== wasImpersonating) {
                        try {
                            await initDatabase();
                            await db.clearUserData(newUserId);
                            console.info('Impersonation boundary crossed — cleared local messaging cache for', newUserId);
                        } catch (error) {
                            console.error('Failed to clear messaging cache on impersonation change:', error);
                        }
                    }
                    await initialize();
                }
            }
        });

        // Watch for SignalR reconnection (sync when connection restored)
        watch(() => signalR.isConnected.value, async (isConnected, wasConnected) => {
            // If we just reconnected (was false, now true) and initialized, sync to catch missed messages
            if (isConnected && wasConnected === false && isInitialized.value) {
                console.info('SignalR reconnected, syncing messages to catch any missed updates...');
                await syncMessages();

                // Resume image upload queue processing
                if (user.value?.id) {
                    console.info('Resuming image upload queue processing...');
                    processImageQueue().catch(error => {
                        console.error('Error processing image queue on reconnection:', error);
                    });
                }
            }
        });

        // Initialize on mount
        onMounted(async () => {
            if (user.value?.id) {
                await initialize();
            }
        });

        // Cleanup on unmount
        onUnmounted(() => {
            cleanup();
        });
    }

    /**
     * Get user from cache
     */
    const getUser = async (userId) => {
        await initDatabase();
        return await db.getUser(userId);
    };

    /**
     * Get multiple users from cache
     */
    const getUsers = async (userIds) => {
        await initDatabase();
        return await db.getUsers(userIds);
    };

    /**
     * Clear all messaging data from IndexedDB
     */
    const clearAllMessagingData = async () => {
        await initDatabase();
        await db.clearAllData();
        // Reset state
        conversations.value = [];
        activeConversationId.value = null;
        activeMessages.value = [];
        typingUsers.value = new Map();
        console.log('All messaging data cleared. Reload the page to re-sync.');
    };

    return {
        // State
        conversations,
        activeConversation,
        activeConversationId,
        activeMessages,
        typingUsers,
        unreadCount,
        syncInProgress,
        isInitialized,
        entityChatLoading,
        entityChatErrors,
        imageUploadQueue,
        processingQueue,

        // Methods
        initialize,
        cleanup,
        syncMessages,
        sendMessage,
        sendImageMessage,
        createConversation,
        createOrOpenEntityChat,
        setActiveConversation,
        updateConversationTitle,
        updateConversationParticipants,
        updateConversationAdmins,
        leaveConversation,
        getConversationNotifications,
        setConversationNotifications,
        deleteConversation,
        deleteFailedMessage,
        deleteSentMessage,
        retrySendMessage,
        sendTypingIndicator,
        loadFromIndexedDB,
        getUser,
        getUsers,
        clearAllMessagingData
    };
};
